import { ChangeEvent } from 'react'
import ObjectID from 'bson-objectid'

import { Document, FormError, Payment } from '../types'
import { config } from '../config'

type Id = string | number
let inactivityTimeout: number

export const holidays = new Set<string>([
  // "2025-01-01", "2025-03-24", ...
])

export const generateObjectId = (): string => {
  return String(ObjectID())
}

export const handleGetIfEmailIsValid = (stringToValidate: string) => {
  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/
  const isEmail = emailRegex.test(stringToValidate)

  return isEmail
}

export const handleGetIfPasswordIsValid = (stringToValidate: string) => {
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*\W)[A-Za-z\d\W]{8,}$/
  const isValidPassword = passwordRegex.test(stringToValidate)

  return isValidPassword
}

export const numberWithCommas = (numberToCast: number | string) => {
  if (!numberToCast) return '0'

  const partsOfNumber = numberToCast.toString().split('.')
  partsOfNumber[0] = partsOfNumber[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  if (String(partsOfNumber[1]) === '' || String(partsOfNumber[1]) === '0') {
    partsOfNumber[1] = '00'
  }

  if (partsOfNumber[1] && String(partsOfNumber[1]) !== '' && String(partsOfNumber[1]) !== '0') {
    partsOfNumber[1] = partsOfNumber[1].slice(0, 2)
  }

  return partsOfNumber.join('.')
}

export const numberWithDots = (numberToCast: number | string) => {
  if (!numberToCast) return '0'

  const partsOfNumber = numberToCast.toString().split('.')
  partsOfNumber[0] = partsOfNumber[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.')

  if (String(partsOfNumber[1]) === '' || String(partsOfNumber[1]) === '0') {
    partsOfNumber[1] = '00'
  }

  return partsOfNumber.join('.')
}

export const createHash = async (stringToHash: string) => {
  const msgUint8 = new TextEncoder().encode(stringToHash)
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
  return hashHex
}

export const handleGetParsedValue = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
  const target = event.target

  if (event.target.value.length === 0 && isNaN(Number(event.target.value))) {
    target.value = ''
    return target.value
  }

  const input = event.target.value.replace(/\D/g, '')

  if (input.length > 0) {
    target.value = `$ ${numberWithCommas(Number(input))}`
  }

  return target.value
}

export const checkIfPaymentIsTotallyReviewed = (paymentData: Payment | Partial<Payment> | null) => {
  let paymentReviewed = true

  if (Array.isArray(paymentData?.novelties)) {
    for (let i = 0; i < paymentData.novelties.length; i++) {
      const paymentNovelty = paymentData.novelties[i]

      if (paymentNovelty.resolved === false) {
        paymentReviewed = false
      }
    }
  }

  return paymentReviewed
}

export const checkIfDocumentIsWithNovelties = (documentData: Document | Partial<Document>) => {
  if (
    documentData.totalOfNoveltiesEmptyBankName === 0 &&
    documentData.totalOfNoveltiesUnknownBankName === 0 &&
    documentData.totalOfNoveltiesWrongDocumentType === 0 &&
    documentData.totalOfNoveltiesEmptyPaymentAmount === 0 &&
    documentData.totalOfNoveltiesWrongPaymentAmount === 0 &&
    documentData.totalOfNoveltiesBackgroundResearch === 0 &&
    documentData.totalOfNoveltiesEmptyRegisteredName === 0 &&
    documentData.totalOfNoveltiesEmptyDocumentNumber === 0 &&
    documentData.totalOfNoveltiesWrongDocumentNumber === 0 &&
    documentData.totalOfNoveltiesEmptyPaymentReference === 0 &&
    documentData.totalOfNoveltiesWrongBankAccountNumber === 0 &&
    documentData.totalOfNoveltiesEmptyBankAccountNumber === 0 &&
    documentData.totalOfNoveltiesDuplicatePaymentReference === 0 &&
    documentData.totalOfNoveltiesDuplicateBankAccountNumber === 0 &&
    documentData.totalOfNoveltiesBankAccountNumberNotRegistered === 0 &&
    documentData.totalOfNoveltiesWrongBankAccountType === 0 &&
    documentData.totalOfNoveltiesEmptyBankAccountType === 0
  ) {
    return false
  }

  return true
}

const handleNormalizeMoneyString = (str: string): { sign: string | null; digits: string; currency: string | null } => {
  // eslint-disable-next-line
  const cleaned = str.replace(/[\s,\.]/g, '')
  const match = cleaned.match(/^([+-]?)(\$?)(\d+)([A-Z]{3})?$/i)

  if (match) {
    return {
      sign: match[1] || null,
      digits: match[3],
      currency: match[4]?.toUpperCase() || null
    }
  }

  return {
    sign: null,
    digits: '',
    currency: null
  }
}

const handleRemoveAccents = (str: string): string => {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

const handleNormalizeDocumentString = (str: string): string => {
  // eslint-disable-next-line
  return str.replace(/[\.\-]/g, '')
}

export const handleIsValidMatch = (input: string, target: string, columnType?: 'document' | 'currency') => {
  const escapeRegExp = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

  if (columnType === 'currency') {
    const inputNorm = handleNormalizeMoneyString(input)
    const targetNorm = handleNormalizeMoneyString(target)

    if (inputNorm.sign && inputNorm.sign !== targetNorm.sign) return false

    if (inputNorm.currency && inputNorm.currency !== targetNorm.currency) return false

    if (inputNorm.digits && targetNorm.digits.includes(inputNorm.digits)) return true

    if (!inputNorm.digits && input.trim()) {
      const pattern = escapeRegExp(input.trim())
      const regex = new RegExp(pattern, 'i')
      return regex.test(target)
    }

    return false
  }

  if (columnType === 'document') {
    const normalizedInput = handleNormalizeDocumentString(input)
    const normalizedTarget = handleNormalizeDocumentString(target)
    return normalizedTarget.includes(normalizedInput)
  }

  const inputNoAccent = handleRemoveAccents(input.toLowerCase())
  const targetNoAccent = handleRemoveAccents(target.toLowerCase())

  const pattern = inputNoAccent
    .split(' ')
    .map((word) => escapeRegExp(word.trim()))
    .filter(Boolean)
    .join('.*')

  const regex = new RegExp(pattern, 'i')
  return regex.test(targetNoAccent)
}

export const handleCheckIfBankAccountNumberIsValid = (currentBankData: { bankAchCode: string }, bankAccountNumber: string, translate: any) => {
  if (currentBankData.bankAchCode === '1507' && bankAccountNumber.length > 11) {
    return translate('bankAccountErrors.errorOne', { ns: 'payments-to-review-management' })
  } else if (currentBankData.bankAchCode === '1051' && bankAccountNumber.length < 11) {
    return translate('bankAccountErrors.errorTwo', { ns: 'payments-to-review-management' })
  } else if (currentBankData.bankAchCode === '1007' && bankAccountNumber.length > 11) {
    return translate('bankAccountErrors.errorThree', { ns: 'payments-to-review-management' })
  } else if (currentBankData.bankAchCode === '1551' && bankAccountNumber.length < 10 && !bankAccountNumber.startsWith('3')) {
    return translate('bankAccountErrors.errorFour', { ns: 'payments-to-review-management' })
  } else if (currentBankData.bankAchCode === '1032' && bankAccountNumber.length > 11) {
    return translate('bankAccountErrors.errorFive', { ns: 'payments-to-review-management' })
  } else if (currentBankData.bankAchCode === '1001' && bankAccountNumber.length > 9) {
    return translate('bankAccountErrors.errorSix', { ns: 'payments-to-review-management' })
  } else if (currentBankData.bankAchCode === '1019' && bankAccountNumber.length > 10) {
    return translate('bankAccountErrors.errorSeven', { ns: 'payments-to-review-management' })
  } else if (currentBankData.bankAchCode === '1013' && bankAccountNumber.length > 16) {
    return translate('bankAccountErrors.errorEight', { ns: 'payments-to-review-management' })
  }

  return ''
}

export const handleCheckIfOtpCodeIsValid = (otpCode: string, translate: any) => {
  let otpCodeIsValid = true
  const formErrors: FormError = {}
  const isNumber = !isNaN(Number(otpCode))

  if (otpCode === '') {
    otpCodeIsValid = false
    formErrors.otpCodeError = true
    formErrors.generalError = translate('otpCodeForm.errors.emptyCode', { ns: 'two-factor-auth' })
  }

  if (otpCode !== '' && otpCode.length < 6) {
    otpCodeIsValid = false
    formErrors.otpCodeError = true
    formErrors.generalError = translate('otpCodeForm.errors.incompleteCode', { ns: 'two-factor-auth' })
  }

  if (otpCode !== '' && otpCode.length === 6 && !isNumber) {
    otpCodeIsValid = false
    formErrors.otpCodeError = true
    formErrors.generalError = translate('otpCodeForm.errors.errorCode', { ns: 'two-factor-auth' })
  }

  return { otpCodeIsValid, formErrors }
}

export const resetInactivityTimer = (logoutCallback: () => void) => {
  clearTimeout(inactivityTimeout)

  inactivityTimeout = window.setTimeout(
    () => {
      // clearEncryptionKey()
      logoutCallback()
    },
    5 * 60 * 1000
  )
}

export const copyTextToClipboard = async (text: string): Promise<void> => {
  if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
    try {
      await navigator.clipboard.writeText(text)
      console.log('Text copied successfully using Clipboard API.')
    } catch (error) {
      console.error('Failed to copy using Clipboard API, falling back to textarea method.', error)
      fallbackCopyTextToClipboard(text)
    }
  } else {
    fallbackCopyTextToClipboard(text)
  }
}

const fallbackCopyTextToClipboard = (text: string): void => {
  const textArea = document.createElement('textarea')
  textArea.value = text

  textArea.style.position = 'fixed'
  textArea.style.top = '0'
  textArea.style.left = '0'
  textArea.style.width = '1px'
  textArea.style.height = '1px'
  textArea.style.padding = '0'
  textArea.style.border = 'none'
  textArea.style.outline = 'none'
  textArea.style.boxShadow = 'none'
  textArea.style.background = 'transparent'

  document.body.appendChild(textArea)
  textArea.focus()
  textArea.select()

  try {
    const successful = document.execCommand('copy')
    console.log(successful ? 'Fallback: Text copied successfully.' : 'Fallback: Unable to copy text.')
  } catch (error) {
    console.error('Fallback: Error occurred while copying text:', error)
  }

  document.body.removeChild(textArea)
}

export const businessDaysAgo = (from: Date, n: number, holidaysISO = new Set<string>()): Date => {
  const d = new Date(from)
  let remaining = n

  while (remaining > 0) {
    d.setDate(d.getDate() - 1)

    const day = d.getDay()
    const iso = d.toISOString().slice(0, 10)

    const isWeekend = day === 0 || day === 6
    const isHoliday = holidaysISO.has(iso)

    if (!isWeekend && !isHoliday) {
      remaining -= 1
    }
  }

  return d
}

export const canDisplayBalancesFromBrankingPartners = (userSession?: { role: string; belongsTo?: string }): boolean => {
  const isClient = userSession?.role.includes('client') ?? false
  const belongsTo = String(userSession?.belongsTo ?? '')

  const isPaxumClient = belongsTo === String(config.app.paxumCompanyId)

  const isDemoPaxumClient = belongsTo === String(config.app.demoPaxumCompanyId)

  if (config.environment !== 'production') {
    if (!isClient) return true

    if (isPaxumClient) return false

    if (isDemoPaxumClient) return false

    return false
  }

  if (config.environment === 'production') {
    if (!isClient) return true

    if (isPaxumClient) return false

    return false
  }

  return false
}

export const canDisplayTotalBalanceBrief = (userSession?: { role: string; belongsTo?: string }): boolean => {
  const role = userSession?.role ?? ''
  const isClient = role.indexOf('client') > -1

  const belongsTo = String(userSession?.belongsTo ?? '')
  const paxumId = String(config.app.paxumCompanyId)
  const demoPaxumId = String(config.app.demoPaxumCompanyId)

  if (!isClient) {
    return true
  }

  if (config.environment !== 'production') {
    return belongsTo === paxumId || belongsTo === demoPaxumId
  }

  return belongsTo === paxumId
}

export const upsertEntityWithLimitAtStart = (arr: readonly any[], item: any, limit: number) => {
  if (!Number.isFinite(limit) || limit <= 0) return []

  const idx = arr.findIndex((x) => x._id === item._id)

  let next: Array<any>

  if (idx >= 0) {
    next = arr.slice()
    next[idx] = item
  } else {
    next = [item, ...arr]
  }

  return next.length > limit ? next.slice(0, limit) : next
}

export const upsertManyWithLimitAtStartFast = (arr: readonly any[], incoming: readonly any[], limit: number) => {
  if (limit <= 0) return []
  if (!incoming || incoming.length === 0) return arr.length > limit ? arr.slice(0, limit) : arr.slice()

  const next = arr.slice()

  const indexById = new Map<Id, number>()
  for (let i = 0; i < next.length; i++) indexById.set(next[i]._id, i)

  const lastById = new Map<Id, any>()
  for (const element of incoming) lastById.set(element._id, element)

  const prepend: any[] = []
  for (const element of incoming) {
    const id = element._id

    if (lastById.get(id) !== element) continue

    const idx = indexById.get(id)
    if (idx === undefined) {
      prepend.push(element)
    } else {
      next[idx] = element
    }
  }

  if (prepend.length === 0) {
    return next.length > limit ? next.slice(0, limit) : next
  }

  const combined = prepend.concat(next)
  return combined.length > limit ? combined.slice(0, limit) : combined
}

export const normalize = (s: unknown) =>
  String(s ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()

export const buildTokens = (q: unknown) => normalize(q).split(/\s+/).filter(Boolean)

export const matchesAllTokens = (haystack: string, tokens: string[]) => {
  if (tokens.length === 0) return true
  return tokens.every((t) => haystack.includes(t))
}
